import { useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { RefreshCw, RotateCcw, Trash2 } from 'lucide-react';

export type IncidentPhotoStatus = 'capturing' | 'uploading' | 'done' | 'error';

export interface IncidentPhotoSlotProps {
  status: IncidentPhotoStatus;
  thumbnailUrl?: string;
  errorMessage?: string;
  onRemove: () => void;
  onRetake: () => void;
}

/**
 * Single tile in the array-based incident photo grid.
 * Simpler status machine than PhotoCaptureSlot: no empty/capturing tile
 * (those are represented by the shared CameraCapture overlay in the parent) —
 * only uploading | done | error, each with a Remove action (ticket-incident-photos).
 */
export function IncidentPhotoSlot({
  status,
  thumbnailUrl,
  errorMessage,
  onRemove,
  onRetake,
}: IncidentPhotoSlotProps) {
  const { t } = useTranslation('checkin');

  if (status === 'uploading') {
    return (
      <div className="relative aspect-square overflow-hidden rounded-sm border border-border">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt={t('incidentPhotoSlot.pendingUploadAlt')}
            className="h-full w-full object-cover"
          />
        )}
        <div
          role="status"
          className="absolute inset-0 flex items-center justify-center bg-black/50"
        >
          <span className="jq-caption text-white">{t('incidentPhotoSlot.uploading')}</span>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex aspect-square min-h-[72px] flex-col items-center justify-center gap-1 rounded-sm border border-destructive/60 bg-destructive/10 px-2">
          <span className="text-[11px] text-center text-destructive">
            {errorMessage ?? t('incidentPhotoSlot.uploadFailed')}
          </span>
        </div>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            className="h-8 flex-1 text-[11px] text-destructive"
            onClick={onRetake}
            aria-label={t('incidentPhotoSlot.retryAria')}
          >
            <RefreshCw className="size-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-8 flex-1 text-[11px] text-destructive"
            onClick={onRemove}
            aria-label={t('incidentPhotoSlot.removeAria')}
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      </div>
    );
  }

  // ── Done ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-1">
      <div className="relative aspect-square overflow-hidden rounded-sm border border-border">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt={t('incidentPhotoSlot.incidentAlt')}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="flex gap-1">
        <Button
          type="button"
          variant="ghost"
          className="h-8 flex-1 text-[11px]"
          onClick={onRetake}
          aria-label={t('incidentPhotoSlot.retakeAria')}
        >
          <RotateCcw className="size-3" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-8 flex-1 text-[11px]"
          onClick={onRemove}
          aria-label={t('incidentPhotoSlot.removeAria')}
        >
          <Trash2 className="size-3" />
        </Button>
      </div>
    </div>
  );
}
